import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import RefundService, { IRefund, IRefundDetailItem } from "../../services/RefundService";
import OrderService, { IOrder, IOrderDetail } from "../../services/OrderService";
import { formatDateArray, getErrorMessage } from "../../services/apiUtils";
import { isEditable, refundStatusLabel, refundStatusColor as statusColor } from "../../services/refundStatusUtils";
import { theme } from "../../theme";

type RefundDetailDialogProps = {
  refundId: number | null;
  onClose: () => void;

  onProcessed?: () => void;

  listStatus?: string;
};

type ItemDraft = {
  refundDetailId: number;
  quantityRefunded: number;
  refundStatus: "FULL_REFUNDED" | "PARTIALLY_REFUNDED" | "REJECTED";
  message: string;
};

const normalizeItem = (
  raw: any,
  idx: number,
  refundReason?: string,
  orderDetail?: IOrderDetail
): IRefundDetailItem => ({
  refundDetailId: raw.refundDetailId ?? raw.id ?? raw.orderDetailId ?? idx,
  orderDetailId: raw.orderDetailId ?? orderDetail?.orderDetailId,
  productId: orderDetail?.productId ?? raw.productId,
  productName:
    raw.productName ?? raw.productTitle ?? raw.productDesc ??
    orderDetail?.productName ?? orderDetail?.productDesc ??
    `Item ${idx + 1}`,
  productImgUrl: raw.productImgUrl ?? raw.imageUrl ?? orderDetail?.productImgUrl,
  reason: raw.reason ?? raw.refundReason ?? refundReason ?? "-",
  quantityOrdered: raw.quantityOrdered ?? raw.productQty ?? orderDetail?.productQty,
  quantityRefunded: raw.quantityRefunded ?? raw.qty ?? 1,
  unitPrice: raw.unitPrice ?? raw.productPrice ?? raw.amount ?? orderDetail?.productPrice,
  refundStatus: raw.refundStatus ?? raw.status,

  processedMessage: raw.message ?? raw.staffMessage ?? raw.processedMessage,
});

const pickCustomerName = (body: any, order?: IOrder | null): string | undefined =>
  order?.userFName ||
  order?.userName ||
  body.userName ||
  body.customerName ||
  body.userFullName ||
  body.user?.name ||
  body.user?.userName ||
  body.order?.userName ||
  body.order?.userFName ||
  body.order?.customerName;

const pickCustomerEmail = (body: any, order?: IOrder | null): string | undefined =>
  order?.userEmail ||
  body.userEmail ||
  body.customerEmail ||
  body.user?.email ||
  body.order?.userEmail;

const pickOrderNumber = (body: any, order?: IOrder | null): string | undefined =>
  order?.orderNumber || body.orderNumber || body.order?.orderNumber;

const RefundDetailDialog: React.FC<RefundDetailDialogProps> = ({
  refundId,
  onClose,
  onProcessed,
  listStatus,
}) => {
  const [refund, setRefund] = useState<IRefund | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, ItemDraft>>({});

  const open = refundId != null;

  useEffect(() => {
    if (refundId == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRefund(null);

    const load = async () => {
      try {
        const res: any = await RefundService.getRefundById(refundId);
        const body = res?.data?.data ?? res?.data?.body ?? res?.data ?? {};

        let order: IOrder | null = null;
        if (body.orderId != null) {
          try {
            const orderRes: any = await OrderService.getOrderById(body.orderId);
            order = orderRes?.data?.data ?? orderRes?.data?.body ?? orderRes?.data ?? null;
          } catch {
            order = null;
          }
        }
        if (cancelled) return;

        const orderDetailsById = new Map<number, IOrderDetail>(
          (order?.orderDetails || []).map((od: IOrderDetail) => [od.orderDetailId as number, od])
        );

        const rawItems = body.items || body.refundDetails || body.details || [];
        const items = rawItems.map((it: any, idx: number) =>
          normalizeItem(it, idx, body.reason, orderDetailsById.get(it.orderDetailId))
        );
        const normalized: IRefund = {
          ...body,

          status: body.status ?? listStatus,
          items,
          userName: pickCustomerName(body, order),
          userEmail: pickCustomerEmail(body, order),
          orderNumber: pickOrderNumber(body, order),
        };
        setRefund(normalized);

        const stillOpen = isEditable(normalized.status);
        const fallbackStatus = normalized.status?.toUpperCase() === "FULL_REFUND_REQUESTED"
          ? "FULL_REFUNDED"
          : "PARTIALLY_REFUNDED";
        const initialDrafts: Record<number, ItemDraft> = {};
        items.forEach((it: IRefundDetailItem) => {
          const itemStatus = (it.refundStatus || "").toUpperCase();
          const seeded: ItemDraft["refundStatus"] =
            itemStatus === "FULL_REFUNDED" || itemStatus === "PARTIALLY_REFUNDED" || itemStatus === "REJECTED"
              ? (itemStatus as ItemDraft["refundStatus"])
              : stillOpen
              ? fallbackStatus
              : "REJECTED";
          initialDrafts[it.refundDetailId] = {
            refundDetailId: it.refundDetailId,
            quantityRefunded: it.quantityRefunded || 1,
            refundStatus: seeded,
            message: "",
          };
        });
        setDrafts(initialDrafts);
      } catch (err: any) {
        if (!cancelled) {
          setError(getErrorMessage(err, "Could not load this refund's details."));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [refundId, listStatus]);

  const items = refund?.items || [];
  const stillOpen = isEditable(refund?.status);

  const overallStatus: "FULL_REFUNDED" | "PARTIALLY_REFUNDED" | "REJECTED" = useMemo(() => {
    const statuses = Object.values(drafts).map((d) => d.refundStatus);
    if (statuses.length > 0 && statuses.every((s) => s === "REJECTED")) return "REJECTED";
    if (statuses.length > 0 && statuses.every((s) => s === "FULL_REFUNDED")) return "FULL_REFUNDED";
    return "PARTIALLY_REFUNDED";
  }, [drafts]);

  const updateDraft = (refundDetailId: number, patch: Partial<ItemDraft>) => {
    setDrafts((prev) => ({
      ...prev,
      [refundDetailId]: { ...prev[refundDetailId], ...patch },
    }));
  };

  const missingMessageIds = items
    .filter((it: IRefundDetailItem) => !(drafts[it.refundDetailId]?.message || "").trim())
    .map((it: IRefundDetailItem) => it.refundDetailId);
  const hasMissingMessages = missingMessageIds.length > 0;

  const submit = async (forceReject: boolean) => {
    if (!refund) return;
    if (hasMissingMessages) {
      setError("Har item ke liye message likhna zaroori hai customer ko bhejne se pehle.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payloadItems = items.map((it: IRefundDetailItem) => {
        const draft = drafts[it.refundDetailId];
        return {
          refundDetailId: it.refundDetailId,
          quantityRefunded: draft?.quantityRefunded ?? it.quantityRefunded ?? 1,
          refundStatus: forceReject ? "REJECTED" : draft?.refundStatus ?? "PARTIALLY_REFUNDED",
          message: draft?.message?.trim(),
        };
      });
      await RefundService.processRefund({
        refundId: refund.refundId,
        refundStatus: forceReject ? "REJECTED" : overallStatus,
        items: payloadItems,
      });
      onProcessed?.();
      onClose();
    } catch (err: any) {
      setError(getErrorMessage(err, "Could not process this refund."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FDB44D 100%)`,
          color: "#fff",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
          <CurrencyExchangeIcon />
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" fontWeight="bold" noWrap>
            Refund #{refundId}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }} noWrap>
            {refund?.orderNumber ? `Order ${refund.orderNumber}` : refund?.orderId ? `Order #${refund.orderId}` : ""}
          </Typography>
        </Box>
        {refund?.status && (
          <Chip
            label={refundStatusLabel(refund.status)}
            color={statusColor(refund.status) as any}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && refund && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
                gap: 2,
                mb: 3,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Customer
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {refund.userName || refund.userEmail || "-"}
                </Typography>
                {refund.userName && refund.userEmail && (
                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                    {refund.userEmail}
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="body2">
                  {refund.totalAmount != null ? `Rs. ${refund.totalAmount}` : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Requested
                </Typography>
                <Typography variant="body2">{formatDateArray(refund.requestedAt)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Processed
                </Typography>
                <Typography variant="body2">{formatDateArray(refund.processedAt)}</Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Items
            </Typography>

            {items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No item-level detail was returned for this refund.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: 13 }}>Item</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>Reason</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>Qty</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{stillOpen ? "Decision" : "Outcome"}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{stillOpen ? "Message *" : "Message"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((it: IRefundDetailItem) => {
                    const draft = drafts[it.refundDetailId];
                    if (!draft) return null;
                    return (
                      <TableRow key={it.refundDetailId}>
                        <TableCell sx={{ fontSize: 13, maxWidth: 200 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar
                              variant="rounded"
                              src={it.productImgUrl || undefined}
                              sx={{ width: 32, height: 32, bgcolor: "grey.200" }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" noWrap title={it.productName}>
                                {it.productName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {it.productId != null ? `Product #${it.productId}` : ""}
                                {it.productId != null && it.orderDetailId != null ? " · " : ""}
                                {it.orderDetailId != null ? `Order item #${it.orderDetailId}` : ""}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13, maxWidth: 180 }}>
                          <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                            {it.reason || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {stillOpen ? (
                            <TextField
                              type="number"
                              size="small"
                              value={draft.quantityRefunded}
                              inputProps={{ min: 1, max: it.quantityOrdered || undefined }}
                              sx={{ width: 70 }}
                              onChange={(e) =>
                                updateDraft(it.refundDetailId, {
                                  quantityRefunded: Math.max(1, Number(e.target.value) || 1),
                                })
                              }
                            />
                          ) : (
                            <Typography variant="body2">{draft.quantityRefunded}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {stillOpen ? (
                            <TextField
                              select
                              size="small"
                              value={draft.refundStatus}
                              sx={{ width: 160 }}
                              onChange={(e) =>
                                updateDraft(it.refundDetailId, {
                                  refundStatus: e.target.value as ItemDraft["refundStatus"],
                                })
                              }
                            >
                              <MenuItem value="FULL_REFUNDED">Full refund</MenuItem>
                              <MenuItem value="PARTIALLY_REFUNDED">Partial refund</MenuItem>
                              <MenuItem value="REJECTED">Reject</MenuItem>
                            </TextField>
                          ) : (
                            <Chip
                              label={refundStatusLabel(it.refundStatus)}
                              color={statusColor(it.refundStatus) as any}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 13 }}>
                          {stillOpen ? (
                            <TextField
                              size="small"
                              placeholder="Note for the customer"
                              fullWidth
                              required
                              value={draft.message}
                              error={!(draft.message || "").trim()}
                              helperText={!(draft.message || "").trim() ? "Required" : ""}
                              onChange={(e) => updateDraft(it.refundDetailId, { message: e.target.value })}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {it.processedMessage || "-"}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </DialogContent>

      {!loading && refund && (
        <DialogActions sx={{ px: 3, pb: 2.5, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
          {isEditable(refund.status) && hasMissingMessages && (
            <Typography variant="caption" color="error" sx={{ alignSelf: "flex-end", pr: 0.5 }}>
              Process karne se pehle har item ka message bharna zaroori hai.
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} variant="text" disabled={saving}>
            Close
          </Button>
          {isEditable(refund.status) && (
            <>
              <Button
                onClick={() => submit(true)}
                variant="outlined"
                color="error"
                disabled={saving || hasMissingMessages}
              >
                {saving ? <CircularProgress size={18} /> : "Reject Refund"}
              </Button>
              <Button
                onClick={() => submit(false)}
                variant="contained"
                color="success"
                disabled={saving || hasMissingMessages}
              >
                {saving ? <CircularProgress size={18} /> : "Process Refund"}
              </Button>
            </>
          )}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default RefundDetailDialog;
